/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import {
    ClusterService,
    ConsoleApiService,
    FirewallApiService,
    HostnameApiService,
    KeyboardApiService,
    LbErrorService,
    ProxyApiService,
    SmtpApiService
} from '../../../shared';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingService, LogService, RbacService, UtilsService, ValidationService } from '../../../../../core/services';
import { complexPasswordValidation, nameValidation } from '../../../../../core/directives/validation';
import { domainValidation, hostnameValidation, portRangeValidation } from '../../../../../core/directives';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-physical-advanced-config',
    templateUrl: './physicalAdvConf.component.html',
    styleUrls: ['./physicalAdvConf.component.scss']
})

export class PhysicalAdvConfComponent extends BaseComponent {
    /**
     * Form for hostname.
     */
    public hostnameForm: FormGroup;

    /**
     * Form for HTTP Proxy.
     */
    public httpProxyForm: FormGroup;

    /**
     * Form for Console.
     */
    public consoleForm: FormGroup;

    /**
     * Form for Firewall.
     */
    public firewallForm: FormGroup;

    /**
     * Form for Keymap.
     */
    public keyMapForm: FormGroup;

    /**
     * Form for SMTP.
     */
    public smtpForm: FormGroup;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        keyMap: []
    };

    /**
     * Flag to store if the page has loaded or not
     */
    public isLoaded: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the proxy data.
     */
    private proxyData: any;

    /**
     * To store the hostname data.
     * @private
     */
    private hostnameData: any;

    /**
     * To store the console data.
     */
    private consoleData: any;

    /**
     * To store the firewall data.
     */
    private firewallData: any;

    /**
     * To store the key maps list for the dropdown.
     */
    private keyMapsList: any[];

    /**
     * To store the key map data.
     */
    private keyMapData: any;

    /**
     * To store the smtp data.
     */
    private smtpData: any;

    /**
     * To store the initial hostname.
     * @private
     */
    private initialHostnameFormData: any = {};

    /**
     * To store the initial ha proxy data.
     * @private
     */
    private initialHaProxyFormData: any = {};

    /**
     * Store the initial console state.
     * @private
     */
    private initialConsoleFormData: any = {};

    /**
     * To store the initial console from data.
     * @private
     */
    private initialFirewallFormData: any = {};

    /**
     * To store the initial keymap from data.
     * @private
     */
    private initialKeymapFormData: any = {};

    /**
     * To store the initial SMTP from data.
     * @private
     */
    private initialSMTPFormData: any = {};

    /**
     * Store the changed forms.
     * @private
     */
    private changedForms: any = {};

    constructor(
        private clusterService: ClusterService,
        private consoleApiService: ConsoleApiService,
        private firewallApiService: FirewallApiService,
        private formBuilder: FormBuilder,
        private hostnameApiService: HostnameApiService,
        private keyboardApiService: KeyboardApiService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private proxyApiService: ProxyApiService,
        private rbacService: RbacService,
        private smtpApiService: SmtpApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpFormGroups();
        this.setUpFormSubscribers();
        this.setUpPageData();
        this.registerButtons();

        // Disable hostname form if appliance is in a cluster.
        if (this.clusterService.isNodeInCluster()) {
            this.hostnameForm.disable();
        }
    }

    /**
     * A check to determine if the button is disabled or not.
     */
    public isButtonDisabled(): boolean {
        return (
            (this.hostnameForm.invalid || this.hostnameForm.pristine) &&
            (this.httpProxyForm.invalid || this.httpProxyForm.pristine) &&
            (this.consoleForm.invalid || this.consoleForm.pristine) &&
            (this.firewallForm.invalid || this.firewallForm.pristine) &&
            (this.keyMapForm.invalid || this.keyMapForm.pristine) &&
            (this.smtpForm.invalid || this.smtpForm.pristine)
        );

    }

    /**
     * Group submit function.
     */
    public submitForms(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.isLoaded = false;

            forkJoin(this.getApiCallsForChangedForms())
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PhysicalAdvConfComponent: submitForms ', error.message);
                        observer.error();
                    },
                    complete: (): void => {
                        this.clearPasswordFieldOnComplete();
                        this.changedForms = {};
                        this.loadingService.loadingButtonEmitter.next(false);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Clear password field.
     * @private
     */
    private clearPasswordFieldOnComplete(): void {
        const consolePasswordFormField = this.consoleForm.get('password');
        const consoleActiveFormField = this.consoleForm.get('active').value;
        if (!consoleActiveFormField && consolePasswordFormField.value.length) {
            consolePasswordFormField.setValue(null, { emitEvent: false });
            this.initialConsoleFormData.password = null;
        }
    }

    /**
     * Get API calls for changed forms.
     * @private
     */
    private getApiCallsForChangedForms(): any {
        const apiCalls = {};
        if (this.changedForms['hostnameForm']) {
            apiCalls['hostnameForm'] = this.submitHostname();
        }

        if (this.changedForms['consoleForm']) {
            apiCalls['consoleForm'] = this.submitConsole();
        }

        if (this.changedForms['httpProxyForm']) {
            apiCalls['httpProxyForm'] = this.submitHttpProxy();
        }

        if (this.changedForms['smtpForm']) {
            apiCalls['smtpForm'] = this.submitSmtp();
        }

        if (this.changedForms['firewallForm']) {
            apiCalls['firewallForm'] = this.submitFirewall();
        }

        if (this.changedForms['keyMapForm']) {
            apiCalls['keyMapForm'] = this.submitKeyMap();
        }

        return apiCalls;
    }

    /**
     * Submit hostname.
     * @private
     */
    private submitHostname(): Observable<any> {
        return new Observable((observer) => {
            const data = this.hostnameForm.value;
            this.hostnameApiService.addHostname(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        // Update the cluster service with the new hostname.
                        this.clusterService.updateLocalNode('hostname', data.hostname);
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.hostnameForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitHostname', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit console
     * @private
     */
    private submitConsole(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.consoleForm.value;
            this.consoleApiService.set(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.consoleForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitConsole', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit http proxy.
     * @private
     */
    private submitHttpProxy(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.formatSmtpHttpData('httpProxyForm', this.httpProxyForm);
            this.proxyApiService.putProxy(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.httpProxyForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitHttpProxy', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit smtp.
     * @private
     */
    private submitSmtp(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.formatSmtpHttpData('smtpForm', this.smtpForm);
            this.smtpApiService.putSmtp(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.smtpForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitSmtp', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit firewall
     * @private
     */
    private submitFirewall(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.firewallForm.value;
            this.firewallApiService.putFirewall(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.firewallForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitFirewall', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Submit keymap
     * @private
     */
    private submitKeyMap(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const data = this.keyMapForm.value;
            this.keyboardApiService.putKeymap(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.keyMapForm, error.error.errors);
                        this.logService.error('PhysicalAdvConf: submitKeyMap', 'Error submitting data', error);
                        observer.error(error);
                    },
                })
                .add((): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Setup all the forms for the page.
     * @private
     */
    private setUpFormGroups(): void {
        this.hostnameForm = this.formBuilder.group({
            hostname: ['', [Validators.required, hostnameValidation, Validators.maxLength(63)]],
            domain: ['', [Validators.required, domainValidation, Validators.maxLength(253)]]
        });

        this.httpProxyForm = this.formBuilder.group({
            enable: [false],
            address: [null, [Validators.required, ValidationService.ipDomainValidator]],
            port: [null, [Validators.required, portRangeValidation]],
            username: [null, [Validators.required, nameValidation]],
            password: [null, [Validators.required]]
        });

        this.consoleForm = this.formBuilder.group({
            active: [false],
            password: [null],
        });

        this.firewallForm = this.formBuilder.group({
            size: [null, [Validators.required, Validators.min(10000), Validators.max(1000000)]]
        });

        this.keyMapForm = this.formBuilder.group({
            keymap: [null]
        });

        this.smtpForm = this.formBuilder.group({
            enable: [true],
            address: [null, [Validators.required, ValidationService.ipDomainValidator]],
            port: [null, [Validators.required, portRangeValidation]],
            username: [null, [Validators.required, nameValidation]],
            password: [null, [Validators.required]]
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            hostname: this.hostnameApiService.getHostname(),
            httpProxy: this.proxyApiService.getProxy(),
            firewall: this.firewallApiService.getFirewall(),
            console: this.consoleApiService.get(),
            keymaps: this.keyboardApiService.getKeymaps(),
            keymap: this.keyboardApiService.getKeymap(),
            smtp: this.smtpApiService.getSmtp()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.hostnameData = dataGroups['hostname'];
                    this.proxyData = dataGroups['httpProxy'];
                    this.consoleData = dataGroups['console'];
                    this.firewallData = dataGroups['firewall'];
                    this.keyMapsList = dataGroups['keymaps'];
                    this.keyMapData = dataGroups['keymap'];
                    this.smtpData = dataGroups['smtp'];

                    this.patchDataToForm();
                    this.convertKeymapToDropdown();
                    this.isLoaded = true;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PhysicalAdvConfComponent setUpPageData ', error.message);
                }
            });
    }

    /**
     * Patch data to forms.
     * @private
     */
    private patchDataToForm(): void {
        this.hostnameForm.patchValue(this.hostnameData);
        this.hostnameForm.markAsPristine();
        this.initialHostnameFormData = this.hostnameForm.value;

        const proxyPatch = {
            enable: this.proxyData.enable,
            address: this.proxyData.address.value,
            port: this.proxyData.address.port,
            username: this.proxyData.username,
            password: this.proxyData.password
        };
        this.httpProxyForm.patchValue(proxyPatch);
        this.updateFormEnabledState(this.httpProxyForm, this.proxyData.enable);
        this.httpProxyForm.markAsPristine();
        this.initialHaProxyFormData = this.httpProxyForm.value;

        const consolePasswordField = this.consoleForm.get('password');
        this.consoleData['password'] = consolePasswordField.value ? consolePasswordField.value : null;
        this.consoleForm.patchValue(this.consoleData);
        if (!this.consoleData['active']) {
            consolePasswordField.disable({ emitEvent: false });
        }

        this.consoleForm.markAsPristine();
        this.initialConsoleFormData = this.consoleData;

        this.firewallForm.patchValue(this.firewallData);
        this.firewallForm.markAsPristine();
        this.initialFirewallFormData = this.firewallForm.value;

        this.keyMapForm.patchValue({ keymap: this.keyMapData });
        this.keyMapForm.markAsPristine();
        this.initialKeymapFormData = this.keyMapForm.value;

        const smtpPatch = {
            enable: this.smtpData.enable,
            address: this.smtpData.address.value === null ? '' : this.smtpData.address.value,
            port: this.smtpData.address.port,
            username: this.smtpData.username,
            password: this.smtpData.password
        };

        this.smtpForm.patchValue(smtpPatch);
        this.updateFormEnabledState(this.smtpForm, this.smtpData.enable);
        this.smtpForm.markAsPristine();
        this.initialSMTPFormData = this.smtpForm.value;
    }

    /**
     * Update the enabled state of the HTTP proxy/SMTP relay form.
     * @param form
     * @param enabled
     * @private
     */
    private updateFormEnabledState(form: FormGroup, enabled: boolean): void {
        if (enabled) {
            form.get('address').enable();
            form.get('port').enable();
            form.get('username').enable();
            form.get('password').enable();
        } else {
            form.get('address').disable();
            form.get('port').disable();
            form.get('username').disable();
            form.get('password').disable();
        }
    }

    /**
     * Format the smtp/http proxy data for submission to API.
     * @param formName
     * @param formRaw
     * @private
     */
    private formatSmtpHttpData(formName: any, formRaw: any): any {
        const formValues = formRaw.value;
        let formData = {
            enable: formValues?.enable,
            address: {
                value: formValues.address,
                port: formValues.port
            },
            username: formValues.username,
            password: formValues.password,
        };

        if (!formValues?.enable) {
            formData = this.resetHttpSmtpData(formName);
            const form: FormGroup = formName === 'smtpForm' ? this.smtpForm : this.httpProxyForm;
            form.markAsUntouched();
        }

        return formData;
    }

    /**
     * Reset the http or smtp data.
     * @param formName
     * @private
     */
    private resetHttpSmtpData(formName: string): any {
        return {
            enable: false,
            address: formName === 'smtpForm' ? { value: '', port: 25 } : null,
            username: '',
            password: ''
        };
    }

    /**
     * Convert keymap value to the dropdown value for the dropdown component.
     * @private
     */
    private convertKeymapToDropdown(): void {
        const convertedArray: any[] = [];
        for (const keymap of Object.keys(this.keyMapsList)) {
            const labels = this.keyMapsList[keymap];
            for (const label of labels) {
                convertedArray.push(<DropdownModel>{
                    key: label,
                    value: label
                });
            }
        }
        this.dropdown.keyMap = convertedArray;
    }

    /**
     * Toggle validation for the console password depending on if it is enabled
     * and disable the password input if it is no enabled.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.hostnameForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('hostnameForm', () => {
                        const formHasChanged = this.utilsService.hasFormDataChanged(this.initialHostnameFormData, form);
                        this.changedForms['hostnameForm'] = formHasChanged;
                        if (!formHasChanged) {
                            this.hostnameForm.markAsPristine();
                        } else {
                            this.hostnameForm.markAsDirty();
                        }
                    });
                }
            });

        this.httpProxyForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('httpProxyForm', () => {
                        const formHasChanged = this.utilsService.hasFormDataChanged(this.initialHaProxyFormData, form);
                        this.changedForms['httpProxyForm'] = formHasChanged;
                        if (!formHasChanged) {
                            this.httpProxyForm.markAsPristine();
                        } else {
                            this.httpProxyForm.markAsDirty();
                        }
                    });
                }
            });

        this.httpProxyForm.get('enable').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.updateFormEnabledState(this.httpProxyForm, value);
                }
            });

        this.consoleForm.get('active').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: boolean) => {
                    const formHasChanged: boolean = this.utilsService.hasFormDataChanged(this.initialConsoleFormData, { active: value });
                    this.changedForms['consoleForm'] = formHasChanged;
                    const consolePasswordField = this.consoleForm.get('password');
                    if (value) {
                        consolePasswordField.enable({ emitEvent: false });
                        consolePasswordField.setValidators([Validators.required, complexPasswordValidation]);
                    } else {
                        consolePasswordField.disable({ emitEvent: false });
                    }

                    consolePasswordField.updateValueAndValidity({ emitEvent: false });

                    if (!formHasChanged) {
                        this.consoleForm.markAsPristine();
                    } else {
                        this.consoleForm.markAsDirty();
                    }
                }
            });

        this.consoleForm.get('password').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: string) => {
                    const formHasChanged: boolean = this.utilsService.hasFormDataChanged(this.initialConsoleFormData, { password: value });
                    this.changedForms['consoleForm'] = formHasChanged;

                    if (!formHasChanged) {
                        this.consoleForm.markAsPristine();
                    } else {
                        this.consoleForm.markAsDirty();
                    }
                }
            });

        this.firewallForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    if (this.isLoaded) {
                        this.utilsService.debounce('firewallForm', () => {
                            this.firewallForm.get('size').markAsTouched();
                            const formHasChanged = this.utilsService.hasFormDataChanged(this.initialFirewallFormData, form);
                            this.changedForms['firewallForm'] = formHasChanged;
                            if (!formHasChanged) {
                                this.firewallForm.markAsPristine();
                            } else {
                                this.firewallForm.markAsDirty();
                            }
                        });
                    }
                }
            });

        this.smtpForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('smtpForm', () => {
                        const formHasChanged = this.utilsService.hasFormDataChanged(this.initialSMTPFormData, form);
                        this.changedForms['smtpForm'] = formHasChanged;
                        if (!formHasChanged) {
                            this.smtpForm.markAsPristine();
                        } else {
                            this.smtpForm.markAsDirty();
                        }
                    });
                }
            });

        this.smtpForm.get('enable').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: enabled => {
                    this.updateFormEnabledState(this.smtpForm, enabled);
                }
            });

        this.keyMapForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('keyMapForm', () => {
                        const formHasChanged = this.utilsService.hasFormDataChanged(this.initialKeymapFormData, form);
                        this.changedForms['keyMapForm'] = formHasChanged;
                        if (!formHasChanged) {
                            this.keyMapForm.markAsPristine();
                        } else {
                            this.keyMapForm.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'update-config': [{ path: '/', methods: ['ANY'] }],
            'hostname': [{ path: '/system/hostname', methods: ['PUT'] }],
            'proxy': [{ path: '/system/proxy', methods: ['PUT'] }],
            'console': [{ path: '/system/console', methods: ['PUT'] }],
            'firewall': [{ path: '/system/conntrack', methods: ['PUT'] }],
            'keymap': [{ path: '/system/keymap', methods: ['PUT'] }],
            'mail': [{ path: '/system/mail', methods: ['PUT'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup.hostname) {
            this.hostnameForm.disable();
        }

        if (!this.permissionsLookup.proxy) {
            this.httpProxyForm.disable();
        }

        if (!this.permissionsLookup.console) {
            this.consoleForm.disable();
        }

        if (!this.permissionsLookup.firewall) {
            this.firewallForm.disable();
        }

        if (!this.permissionsLookup.keymap) {
            this.keyMapForm.disable();
        }

        if (!this.permissionsLookup.mail) {
            this.smtpForm.disable();
        }
    }
}
